import React from "react";

import { FormattedTimestampText } from "@/shared/components/RobotoMarkdown/FormattedTimestampText";
import {
  HoverActionToolbar,
  hoverActions,
} from "@/shared/components/RobotoMarkdown/HoverActionToolbar";
import {
  useJumpToTimestampAction,
  useSetAnchorAction,
} from "@/shared/components/RobotoMarkdown/timeFormat";

/**
 * Renders an AI-emitted bare timestamp mention (originally written as
 * `t=<n><unit>` in prose — optionally wrapped in an inline-code highlight)
 * as non-clickable inline text, formatted in the user's chosen
 * TimestampFormat.
 *
 * Inserted by the remarkTimestampMentions plugin via the `ai-ts:<ns>` (plain)
 * or `ai-ts-hl:<ns>` (highlighted) URL scheme. Distinct from {@link Timestamp}
 * (which renders an `<a>` with seek behavior for explicit
 * `[label](timestamp:<ns>)` links) — bare mentions are reformatted text only,
 * never clickable, though hovering one does float the shared instant actions
 * above it -- "Jump to timestamp" while a visualizer is open, and "Set as
 * t=0": moving the playhead and choosing a t=0 are both things done to the
 * instant the text names, not navigation away from the text.
 */
export const InlineTimestamp: React.FC<{
  ns: bigint;
  highlighted?: boolean;
}> = ({ ns, highlighted = false }) => {
  const jumpAction = useJumpToTimestampAction(ns);
  const setAnchorAction = useSetAnchorAction(ns);
  const Tag = highlighted ? "code" : "span";
  const rawNs = ns.toString();
  // `title` surfaces the raw ns on hover and to assistive tech, so a reader
  // can recover the exact number behind the formatted text.
  const title = `${rawNs} ns`;
  return (
    <HoverActionToolbar actions={hoverActions(jumpAction, setAnchorAction)}>
      <Tag data-roboto-inline-ts={rawNs} title={title}>
        <FormattedTimestampText ns={ns} />
      </Tag>
    </HoverActionToolbar>
  );
};
